import { accessState, type AccessSummary } from "@/lib/effective-access";
import {
  CalendarHeart,
  LayoutDashboard,
  Receipt,
  ListOrdered,
  Apple,
  TestTubes,
  Activity,
  Grid3X3,
  BookOpen,
  User,
  ShoppingCart,
  Brain,
  HelpCircle,
  MessageSquare,
  Crown,
  Watch,
  Sparkles,
  Instagram,
  LifeBuoy,
  Dna,
  FlaskConical,
  ShieldCheck,
  Archive,
  Compass,
  Camera,
  Cable,
  ChefHat,
  Utensils,
  FileUp,
  FileText,
  type LucideIcon,
} from "lucide-react";

export interface NavItem {
  href: string;
  tLabel: string;
  tDesc: string;
  icon: LucideIcon;
}

export interface NavGroup {
  id: string;
  tLabel: string;
  tDesc: string;
  items: readonly NavItem[];
}

// Shared navigation hierarchy for desktop sidebar and mobile More sheet.
// Category order, destination order, labels and descriptions live here so the
// two surfaces can never drift apart.
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    id: "everyday",
    tLabel: "sidebar.groups.everyday",
    tDesc: "sidebar.groups.everydayDesc",
    items: [
      { href: "/dashboard",  tLabel: "sidebar.dashboard",   icon: LayoutDashboard, tDesc: "sidebar.dashboardDesc" },
      { href: "/companion", tLabel: "sidebar.companion", icon: Compass, tDesc: "sidebar.companionDesc" },
      { href: "/plate-log",  tLabel: "sidebar.plateLog",    icon: ListOrdered,     tDesc: "sidebar.plateLogDesc" },
      { href: "/coach-ai",   tLabel: "sidebar.coachAi",     icon: Sparkles,        tDesc: "sidebar.coachAiDesc" },
      { href: "/plate-talk", tLabel: "sidebar.plateTalk",   icon: MessageSquare,   tDesc: "sidebar.plateTalkDesc" },
      { href: "/grocery",    tLabel: "sidebar.grocery",     icon: ShoppingCart,    tDesc: "sidebar.groceryDesc" },
      { href: "/foods",      tLabel: "sidebar.foods",       icon: Apple,           tDesc: "sidebar.foodsDesc" },
      { href: "/recipes", tLabel: "sidebar.recipes", icon: ChefHat, tDesc: "sidebar.foodsDesc" },
      { href: "/reusable-meals", tLabel: "sidebar.reusableMeals", icon: Utensils, tDesc: "sidebar.plateLogDesc" },
      { href: "/nutrition-tools", tLabel: "sidebar.nutritionTools", icon: FileUp, tDesc: "sidebar.groceryDesc" },
    ],
  },
  {
    id: "insights",
    tLabel: "sidebar.groups.insights",
    tDesc: "sidebar.groups.insightsDesc",
    items: [
      { href: "/evidence",   tLabel: "sidebar.evidenceLab", icon: TestTubes, tDesc: "sidebar.evidenceLabDesc" },
      { href: "/metrics",    tLabel: "sidebar.metrics",     icon: Activity,  tDesc: "sidebar.metricsDesc" },
      { href: "/grid",       tLabel: "sidebar.dailyGrid",   icon: Grid3X3,   tDesc: "sidebar.dailyGridDesc" },
      { href: "/smart-swap", tLabel: "sidebar.smartSwapIq", icon: Brain,     tDesc: "sidebar.smartSwapIqDesc" },
      { href: "/nutrition-reports", tLabel: "sidebar.nutritionReports", icon: Activity, tDesc: "sidebar.metricsDesc" },
      { href: "/nutrition-targets", tLabel: "sidebar.nutritionTargets", icon: ShieldCheck, tDesc: "sidebar.smartSwapIqDesc" },
    ],
  },
  {
    id: "healthData",
    tLabel: "sidebar.groups.healthData",
    tDesc: "sidebar.groups.healthDataDesc",
    items: [
      { href: "/oura",            tLabel: "sidebar.oura",           icon: Watch,        tDesc: "sidebar.ouraDesc" },
      { href: "/genetics",        tLabel: "sidebar.genetics",       icon: Dna,          tDesc: "sidebar.geneticsDesc" },
      { href: "/blood-labs",      tLabel: "sidebar.bloodLabs",      icon: FlaskConical, tDesc: "sidebar.bloodLabsDesc" },
      { href: "/health-documents", tLabel: "sidebar.healthDocuments", icon: FileText, tDesc: "sidebar.healthDocumentsDesc" },
      { href: "/womens-health",   tLabel: "sidebar.womensHealth",   icon: CalendarHeart, tDesc: "sidebar.womensHealthDesc" },
      { href: "/snapshots",       tLabel: "sidebar.snapshots",      icon: Archive,      tDesc: "sidebar.snapshotsDesc" },
      { href: "/progress-photos", tLabel: "sidebar.progressPhotos", icon: Camera,       tDesc: "sidebar.progressPhotosDesc" },
      { href: "/glucose",         tLabel: "glucose.title",         icon: Activity,     tDesc: "glucose.mealResponse.title" },
      { href: "/settings/integrations", tLabel: "sidebar.integrations", icon: Cable,    tDesc: "sidebar.integrationsDesc" },
    ],
  },
  {
    id: "learnHelp",
    tLabel: "sidebar.groups.learnHelp",
    tDesc: "sidebar.groups.learnHelpDesc",
    items: [
      { href: "/feature-tour", tLabel: "sidebar.featureTour", icon: Compass,     tDesc: "sidebar.featureTourDesc" },
      { href: "/glossary",     tLabel: "sidebar.glossary",    icon: BookOpen,    tDesc: "sidebar.glossaryDesc" },
      { href: "/inspiration",  tLabel: "sidebar.inspiration", icon: Instagram,   tDesc: "sidebar.inspirationDesc" },
      { href: "/help",         tLabel: "sidebar.helpFaq",     icon: HelpCircle,  tDesc: "sidebar.helpFaqDesc" },
      { href: "/methodology",  tLabel: "sidebar.methodology", icon: ShieldCheck, tDesc: "sidebar.methodologyDesc" },
      { href: "/support",      tLabel: "sidebar.support",     icon: LifeBuoy,    tDesc: "sidebar.supportDesc" },
    ],
  },
  {
    id: "account",
    tLabel: "sidebar.groups.account",
    tDesc: "sidebar.groups.accountDesc",
    items: [
      { href: "/profile",      tLabel: "sidebar.profileLabel", icon: User,  tDesc: "sidebar.profileDesc" },
      { href: "/billing",      tLabel: "sidebar.billing",      icon: Receipt, tDesc: "sidebar.billingDesc" },
      { href: "/subscription", tLabel: "sidebar.subscription", icon: Crown, tDesc: "sidebar.subscriptionDesc" },
    ],
  },
] as const;

export const NAV_ITEMS: readonly NavItem[] = NAV_GROUPS.flatMap((g) => [...g.items]);

// Groups (besides Everyday) that start expanded in full/advanced mode.
// Focused mode starts every secondary group compact; the active destination's
// group is always revealed in both modes.
export const FULL_MODE_DEFAULT_OPEN = new Set(["insights"]);

// The product tour emits this event before it checks its targets. The sidebar
// listens for it and reveals the requested secondary groups so their links are
// mounted in time for Joyride to highlight them.
export const REVEAL_TOUR_NAV_TARGETS_EVENT = "plate-os:reveal-tour-nav-targets";

// Pages that require a Plate OS Pro subscription. Free users still see these
// in the nav (so they know what Pro unlocks) but with a lock indicator; the
// page itself shows the paywall. Plate Log, Dashboard and Foods stay open.
export const PRO_HREFS = new Set([
  "/plate-talk",
  "/coach-ai",
  "/grocery",
  "/evidence",
  "/metrics",
  "/grid",
  "/smart-swap",
  "/genetics",
  "/blood-labs",
  "/snapshots",
  "/oura",
  "/glucose",
  "/settings/integrations",
]);

export function isProUser(user: AccessSummary | undefined): boolean {
  return accessState(user) === "granted";
}

export function findGroupId(href: string): string | undefined {
  return NAV_GROUPS.find((g) => g.items.some((i) => i.href === href))?.id;
}

export function tourId(href: string): string {
  return href.replace("/", "nav-").replace(/\//g, "-") || "nav-home";
}

export function findGroupIdForTourId(id: string): string | undefined {
  return NAV_GROUPS.find((group) => group.items.some((item) => tourId(item.href) === id))?.id;
}
